/** Landing page for the B2B realm, and the prefix that scopes it. */
export const B2B_LANDING_PAGE = "/b2b";

/**
 * Where a B2B sign-in should send the user back to.
 *
 * @returns the current absolute URL when already inside /b2b, otherwise the
 *   B2B landing page
 */
export const b2bReturnUrl = (): string =>
  window.location.pathname.startsWith(B2B_LANDING_PAGE)
    ? window.location.href
    : B2B_LANDING_PAGE;

/**
 * Resolve the post-login target, refusing anything outside the B2B area.
 *
 * `state` round-trips through Keycloak as the absolute URL produced by
 * {@link b2bReturnUrl}, so its path has to be parsed out before it can be
 * tested. Comparing the raw string against "/b2b" never matches an absolute
 * URL, which silently sent every B2B user to the landing page.
 *
 * Parsing the path is not sufficient on its own: `https://evil.example/b2b`
 * also has a `/b2b` pathname, so the origin is checked too and this value is
 * fed straight to `location.replace`.
 *
 * @param state - the `state` value carried on the OIDC user, if any
 * @returns the URL to navigate to after a successful sign-in, always a
 *   same-origin location within /b2b
 */
export const resolveB2bTarget = (state: unknown): string => {
  if (typeof state !== "string" || state.length === 0) return B2B_LANDING_PAGE;

  try {
    const { origin, pathname } = new URL(state, window.location.origin);
    const isSameOrigin = origin === window.location.origin;
    return isSameOrigin && pathname.startsWith(B2B_LANDING_PAGE)
      ? state
      : B2B_LANDING_PAGE;
  } catch {
    return B2B_LANDING_PAGE;
  }
};
